import * as React from "react";
import { useDocument } from "@/hooks/useDocument";
import { useUpdateDocument } from "@/hooks/useUpdateDocument";
import { useUpdateDocumentText } from "@/hooks/useUpdateDocumentText";
import { useUser } from "@/hooks/useUser";
import type { IDocument } from "@/types/document.types";
import { getBackendDocumentId } from "@/utils/document-id.utils";

interface SyncDeps {
  getDocumentText: () => Promise<string | null>;
  getDocumentFile: () => Promise<Blob | null>;
  convertDocxToSfdt: (blob: Blob) => Promise<string>;
  onText: (url: string, text: string) => void;
  onSfdt: (id: string, content: string) => void;
}

/**
 * Fetch the current document content and push both plain text and SFDT
 * representations to the backend.
 */
async function syncContent(
  doc: IDocument,
  deps: SyncDeps,
  isCancelled: () => boolean
): Promise<void> {
  const [currentText, docxBlob] = await Promise.all([
    deps.getDocumentText(),
    deps.getDocumentFile().catch(() => null),
  ]);

  if (isCancelled()) return;

  // Path 1: Upload plain text to upload_text_url.
  // `currentText === null` means Word.run failed — skip so we don't wipe S3.
  // `currentText === ""` means the user genuinely cleared the document —
  // sync the empty state so downstream search/AI context stays accurate.
  if (doc.upload_text_url != null && doc.upload_text_url !== "" && currentText != null) {
    deps.onText(doc.upload_text_url, currentText);
  }

  // Path 2: Convert .docx to SFDT and persist document content.
  if (docxBlob != null) {
    const sfdtContent = await deps.convertDocxToSfdt(docxBlob);
    if (isCancelled()) return;
    const documentId = getBackendDocumentId(doc);
    if (documentId == null) return;
    deps.onSfdt(documentId, sfdtContent);
  }
}

export const useDocumentChangeListener = () => {
  const { user } = useUser();
  const { data: document } = useDocument();
  const updateDocumentMutation = useUpdateDocument();
  const updateDocumentTextMutation = useUpdateDocumentText();

  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the Word event cleanup fn once the async setup resolves. Stored in a
  // ref so the synchronous effect-cleanup callback can always reach it.
  const removeListenerRef = React.useRef<(() => Promise<void>) | null>(null);

  React.useEffect(() => {
    // Cancelled flag prevents mutate() calls after this effect instance has
    // been torn down (component unmount or deps change).
    let cancelled = false;
    const isCancelled = () => cancelled;

    const initDocumentListener = async () => {
      try {
        const { setupDocumentChangeListener } = await import("../utils/document.utils");
        const { getDocumentText, getDocumentFile } = await import("@/taskpane/taskpane");
        const { convertDocxToSfdt } = await import("@/services/document.service");

        if (cancelled) return;

        const deps: SyncDeps = {
          getDocumentText,
          getDocumentFile,
          convertDocxToSfdt,
          onText: (url, text) => updateDocumentTextMutation.mutate({ url, text }),
          onSfdt: (id, content) => updateDocumentMutation.mutate({ id, content }),
        };

        const removeListener = await setupDocumentChangeListener(() => {
          if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

          debounceTimerRef.current = setTimeout(() => {
            const syncDocument = document;
            if (syncDocument == null || getBackendDocumentId(syncDocument) == null) return;
            void syncContent(syncDocument, deps, isCancelled).catch((error) =>
              console.error("Failed to update document content:", error)
            );
          }, 5000);
        });

        if (cancelled) {
          removeListener().catch((err) => console.error("Error removing listener:", err));
          return;
        }
        removeListenerRef.current = removeListener;
      } catch (error) {
        console.error("Failed to setup document change listener:", error);
      }
    };

    if (user) void initDocumentListener();

    return () => {
      cancelled = true;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (removeListenerRef.current) {
        removeListenerRef.current().catch((err) => console.error("Error removing listener:", err));
        removeListenerRef.current = null;
      }
    };
  }, [user, getBackendDocumentId(document), document?.upload_text_url]);
  // Mutations excluded: TanStack Query guarantees stable identity across renders.
};
